import { ForwardedRef, forwardRef, InputHTMLAttributes, KeyboardEvent, useState } from 'react'
import styles from './SuggestionsInput.module.scss'
import { clsx } from 'clsx'
import { useClickOutside } from '@/helpers/useClickOutside'
import ArrowDownIcon from './assets/arrow-down.svg'
import CrossIcon from './assets/cross.svg'

export type Option = {
    label: string
    value: string
    id: string
}

export type SuggestionsInputProps = {
    transparent?: boolean
    variant?: 'small' | 'large'
    fullWidth?: boolean
    options: Option[]
    id?: string
    value: string
    onChange: (value: string) => void
    setSelectedValue?: (value: string) => void
    error?: boolean
} & InputHTMLAttributes<HTMLInputElement>

export const SuggestionsInput = forwardRef(
    (
        {
            variant = 'large',
            fullWidth = false,
            transparent = false,
            error,
            options,
            disabled,
            value,
            onChange,
            setSelectedValue,
            ...props
        }: SuggestionsInputProps,
        ref: ForwardedRef<HTMLInputElement>
    ) => {
        const [isOpen, setIsOpen] = useState(false)
        const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null)
        const wrapperRef = useClickOutside<HTMLDivElement>(() => setIsOpen(false))

        const handleSelect = (selectedValue: Option) => {
            onChange(selectedValue.value)
            setSelectedValue?.(selectedValue.id)
            setIsOpen(false)
        }

        const handleOpen = () => {
            if (!disabled) {
                setIsOpen(true)
            }
        }

        const filteredOptions = options.filter((option) => option.label.toLowerCase().includes(value.toLowerCase()))

        const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
            if (e.key === 'Escape') {
                setIsOpen(false)
            }
            if (e.key === 'ArrowDown') {
                e.preventDefault()
                setHighlightedIndex((prev) => (prev === null || prev === filteredOptions.length - 1 ? 0 : prev + 1))
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault()
                setHighlightedIndex((prev) => (prev === null || prev === 0 ? filteredOptions.length - 1 : prev - 1))
            }
            if (e.key === 'Enter' && highlightedIndex !== null) {
                handleSelect(filteredOptions[highlightedIndex])
            }
        }

        return (
            <div className={styles.dropdownWrapper} onKeyDown={handleKeyDown} ref={wrapperRef}>
                <div className={styles.inputWrapper}>
                    <input
                        {...props}
                        className={clsx(
                            styles.input,
                            styles[variant],
                            { [styles.fullWidth]: fullWidth },
                            { [styles.transparent]: transparent },
                            { [styles.error]: error },
                            { [styles.disabled]: disabled }
                        )}
                        onFocus={handleOpen}
                        value={value}
                        onChange={onChange}
                        ref={ref}
                        autoComplete="off"
                        disabled={disabled}
                    />

                    {isOpen ? (
                        <button onClick={() => onChange('')} type="button" className={styles.crossIconWrapper}>
                            <CrossIcon />
                        </button>
                    ) : (
                        <label htmlFor={props.id} className={clsx(styles.iconWrapper, { [styles.disabled]: disabled })}>
                            <ArrowDownIcon />
                        </label>
                    )}
                </div>

                <ul
                    className={clsx(styles.dropdownOptions, {
                        [styles.dropdownOptionsOpen]: isOpen,
                    })}
                    role="listbox"
                >
                    {filteredOptions.length === 0 && (
                        <li className={styles.noOptions} role="option" aria-disabled>
                            No options available
                        </li>
                    )}
                    {filteredOptions.map((option, index) => (
                        <li
                            key={option.id}
                            role="option"
                            className={clsx(styles.dropdownOption, {
                                [styles.highlighted]: highlightedIndex === index,
                            })}
                            onClick={() => handleSelect(option)}
                            onMouseEnter={() => setHighlightedIndex(index)}
                        >
                            {option.label}
                        </li>
                    ))}
                </ul>
            </div>
        )
    }
)
